import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { getFileUploadConfig } from '../../config/fileUpload';
import { useAttachments } from '../../hooks';
import type { InputActionBarConfig } from '../../components/InputActionBar/InputActionBar.types';
import type { FileUploadConfig } from '../../types/attachments';
import type {
  AttachmentItem,
  SavedAttachment,
  AttachmentDisplayItem
} from '../../types/attachments';
import type { CommentAttachment } from '../CommentList/CommentList.types';
import { generateAttachmentId } from '../../utils/attachmentUtils';
import {
  mergeActionBarWithFileUpload,
  getFileUploadInputPropsFrom
} from '../../utils/fileUploadBarUtils';

export interface UseEditModeFileUploadResult {
  enabled: true;
  fileUploadConfig: FileUploadConfig & {
    maxFiles: number;
    accept: string;
    allowMultipleFiles: boolean;
  };
  allowMultipleFiles: boolean;
  maxFiles: number;
  /** Combined list for bar/popover/carousel: kept existing + new (AttachmentItem). */
  attachments: AttachmentDisplayItem[];
  attachmentCount: number;
  removeFile: (id: string) => void;
  previewOpen: boolean;
  setPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
  carouselOpen: boolean;
  setCarouselOpen: React.Dispatch<React.SetStateAction<boolean>>;
  carouselIndex: number;
  setCarouselIndex: React.Dispatch<React.SetStateAction<number>>;
  keepExpandedForUpload: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  uploadAreaRef: React.RefObject<HTMLDivElement | null>;
  triggerFileSelect: () => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Clear new attachments and close preview/carousel; does not change existing. */
  clearValueAndAttachments: () => void;
  openCarousel: (index: number) => void;
  registerRefocusInput: (fn: (() => void) | null) => void;
  /** Final list for submit: kept existing (CommentAttachment) + new files (File[]). */
  getFinalAttachments: () => (CommentAttachment | File)[];
}

/** Merges base action bar config with edit-mode file-upload state (delegates to shared util for DRY). */
export function getActionBarConfigWithEditFileUpload(
  base: InputActionBarConfig | undefined,
  fileUpload: UseEditModeFileUploadResult
): InputActionBarConfig {
  return mergeActionBarWithFileUpload(base, fileUpload);
}

/** Builds file-upload input props for DivWithTextAreaSuggestions in edit mode (delegates to shared util for DRY). */
export function getFileUploadInputPropsForEdit(
  fileUpload: UseEditModeFileUploadResult
) {
  return getFileUploadInputPropsFrom(fileUpload);
}

function toSavedAttachment(item: CommentAttachment | File): SavedAttachment {
  if (item instanceof File) {
    return { id: generateAttachmentId(), file: item };
  }
  return {
    id: item.id,
    name: item.name,
    url: item.url,
    type: item.type,
    file: item.file
  };
}

/**
 * File upload state for edit mode: existing attachments (with remove) + new files (useAttachments).
 * Reuses useAttachments for new files and mirrors useCommentFormFileUpload API for the action bar.
 */
export function useEditModeFileUpload(
  initialAttachments: (CommentAttachment | File)[] | undefined,
  actionBarConfig: InputActionBarConfig | undefined,
  onCancel: () => void
): UseEditModeFileUploadResult | null {
  const showUpload = actionBarConfig?.showUpload === true;
  const fileUploadConfig = getFileUploadConfig(
    actionBarConfig?.fileUploadConfig
  );

  const allowMultipleFiles = fileUploadConfig.allowMultipleFiles !== false;
  const maxFiles = allowMultipleFiles ? fileUploadConfig.maxFiles ?? 10 : 1;

  const existingList = useMemo(
    () => (initialAttachments ?? []).map(toSavedAttachment),
    [initialAttachments]
  );

  const [removedExistingIds, setRemovedExistingIds] = useState<Set<string>>(
    () => new Set()
  );

  const {
    attachments: newAttachments,
    addFiles,
    removeFile: removeNewFile,
    clearAttachments: clearNewAttachments,
    getFiles,
    attachmentCount: newCount
  } = useAttachments({ maxFiles });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [keepExpandedForUpload, setKeepExpandedForUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAreaRef = useRef<HTMLDivElement | null>(null);
  const refocusInputRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const onWindowFocus = () => setKeepExpandedForUpload(false);
    window.addEventListener('focus', onWindowFocus);
    return () => window.removeEventListener('focus', onWindowFocus);
  }, []);

  const keptExisting = useMemo(
    () => existingList.filter(e => !removedExistingIds.has(e.id)),
    [existingList, removedExistingIds]
  );

  const displayList: AttachmentDisplayItem[] = useMemo(
    () => [...keptExisting, ...newAttachments],
    [keptExisting, newAttachments]
  );

  const attachmentCount = keptExisting.length + newCount;

  const removeFile = useCallback(
    (id: string) => {
      if (removedExistingIds.has(id)) return;
      const isExisting = existingList.some(e => e.id === id);
      if (isExisting) {
        setRemovedExistingIds(prev => new Set(prev).add(id));
      } else {
        removeNewFile(id);
      }
    },
    [existingList, removedExistingIds, removeNewFile]
  );

  const clearValueAndAttachments = useCallback(() => {
    clearNewAttachments();
    setPreviewOpen(false);
    setCarouselOpen(false);
    setRemovedExistingIds(new Set());
    onCancel();
  }, [clearNewAttachments, onCancel]);

  const triggerFileSelect = useCallback(() => {
    setKeepExpandedForUpload(true);
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files?.length) {
        const remaining = Math.max(0, maxFiles - displayList.length);
        if (remaining > 0) {
          addFiles(files, {
            maxSizePerFile: fileUploadConfig.maxSizePerFile
          });
        }
      }
      e.target.value = '';
      setKeepExpandedForUpload(false);
      refocusInputRef.current?.();
    },
    [addFiles, fileUploadConfig.maxSizePerFile, maxFiles, displayList.length]
  );

  const registerRefocusInput = useCallback((fn: (() => void) | null) => {
    refocusInputRef.current = fn;
  }, []);

  const openCarousel = useCallback((index: number) => {
    setCarouselIndex(index);
    setCarouselOpen(true);
    setPreviewOpen(false);
  }, []);

  const getFinalAttachments = useCallback((): (CommentAttachment | File)[] => {
    const kept: CommentAttachment[] = keptExisting.map(e => ({
      id: e.id,
      name: e.name,
      url: e.url,
      type: e.type,
      file: e.file
    }));
    const newFiles = getFiles();
    return [...kept, ...newFiles];
  }, [keptExisting, getFiles]);

  if (!showUpload) return null;

  return {
    enabled: true as const,
    fileUploadConfig,
    allowMultipleFiles,
    maxFiles,
    attachments: displayList,
    attachmentCount,
    removeFile,
    previewOpen,
    setPreviewOpen,
    carouselOpen,
    setCarouselOpen,
    carouselIndex,
    setCarouselIndex,
    keepExpandedForUpload,
    fileInputRef,
    uploadAreaRef,
    triggerFileSelect,
    handleFileInputChange,
    clearValueAndAttachments,
    openCarousel,
    registerRefocusInput,
    getFinalAttachments
  };
}
